import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { 
  Sparkles, 
  Palette, 
  Settings, 
  Users, 
  Target, 
  Eye 
} from "lucide-react";

const benefits = [
  {
    number: "01",
    icon: <Sparkles className="w-6 h-6" />,
    title: "Enfoque Creativo y Estratégico",
    description: "Cada proyecto combina creatividad con una estrategia clara orientada a tus objetivos de negocio.",
  },
  {
    number: "02",
    icon: <Palette className="w-6 h-6" />,
    title: "Diseño Profesional y Moderno",
    description: "Creamos piezas visuales de alta calidad que reflejan profesionalismo y capturan la esencia de tu marca.",
  },
  {
    number: "03",
    icon: <Settings className="w-6 h-6" />,
    title: "Soluciones Personalizadas",
    description: "No usamos plantillas genéricas. Cada solución se adapta específicamente a tus necesidades.",
  },
  {
    number: "04",
    icon: <Users className="w-6 h-6" />,
    title: "Atención Cercana",
    description: "Comunicación directa y transparente en cada etapa del proyecto. Tu visión es nuestra prioridad.",
  },
  {
    number: "05",
    icon: <Target className="w-6 h-6" />,
    title: "Orientación a Resultados",
    description: "Nos enfocamos en métricas reales: engagement, conversiones, posicionamiento y crecimiento.",
  },
  {
    number: "06",
    icon: <Eye className="w-6 h-6" />,
    title: "Experiencia en Imagen Digital",
    description: "Años de experiencia construyendo presencias digitales impactantes y memorables.",
  },
];

export default function BenefitsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section
      id="benefits"
      ref={ref}
      className="py-24 lg:py-32 bg-[#050505] relative overflow-hidden"
      data-testid="benefits-section"
    >
      {/* Decorative Elements - desktop only */}
      <div className="absolute top-1/4 right-0 w-64 h-64 bg-[#D4AF37]/5 rounded-full blur-3xl hidden lg:block" />
      <div className="absolute bottom-1/4 left-0 w-64 h-64 bg-[#FF6B6B]/5 rounded-full blur-3xl hidden lg:block" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-12 lg:mb-20"
        >
          <p className="overline mb-4">¿Por Qué Elegirnos?</p>
          <h2 className="font-playfair text-3xl lg:text-5xl font-bold text-white max-w-2xl">
            Lo que nos hace{" "}
            <span className="italic text-[#FF6B6B]">diferentes</span>
          </h2>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10 lg:gap-y-12">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: index * 0.05, duration: 0.5 }}
              className="relative"
              data-testid={`benefit-${index}`}
            >
              {/* Large Number Background - desktop only */}
              <span className="absolute -top-4 -left-2 font-playfair text-6xl lg:text-7xl font-bold text-white/5 select-none hidden lg:block">
                {benefit.number}
              </span>

              <div className="relative z-10">
                {/* Icon */}
                <div className="w-12 h-12 bg-[#121212] border border-white/10 rounded-lg flex items-center justify-center text-[#D4AF37] mb-4">
                  {benefit.icon}
                </div>

                {/* Title */}
                <h3 className="font-playfair text-lg lg:text-xl font-bold text-white mb-3">
                  {benefit.title}
                </h3>

                {/* Description */}
                <p className="text-[#A3A3A3] text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
